import {
    ApiSocialFriendInviteShareLinkPrepareReqSchema,
    ApiSocialFriendInviteShareLinkPrepareResSchema,
    ApiSocialFriendInviteSharePrepareReqSchema,
    ApiSocialFriendInviteSharePrepareResSchema,
    ApiSocialFriendInviteShareRedeemReqSchema,
    ApiSocialFriendInviteShareRedeemResSchema,
    type ApiSocialFriendInviteShareLinkPrepareErrorCode,
    type ApiSocialFriendInviteSharePrepareErrorCode,
    type ApiSocialFriendInviteShareRedeemErrorCode,
} from "@bridge-applications/pb-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { NAME_SPACE } from "../../state/config";
import { authenticatedApiCall } from "../helpers/api";
import { haptics } from "../helpers/haptics";
import { getTelegramWebApp, shareUrlViaTelegram } from "../helpers/telegram";
import i18n from "../i18n/i18n";

const FRIEND_INVITE_START_PARAM = /^fri_[A-Za-z0-9_-]{30,100}$/;
const FRIEND_INVITE_SHARE_STORAGE_VERSION = "v2";
const FRIEND_INVITE_SHARE_ERROR_TOAST_ID = "friend-invite-share-error";
const FRIEND_INVITE_SHARE_SUCCESS_TOAST_ID = "friend-invite-share-success";
const FRIEND_INVITE_REDEMPTION_TOAST_ID = "friend-invite-redemption";

type FriendInviteSharingTransport = "prepared-message" | "share-link";

interface PreparedFriendInviteBase {
    requestId: string;
    expiresAt: string;
}

interface PreparedFriendInviteMessage extends PreparedFriendInviteBase {
    transport: "prepared-message";
    messageId: string;
}

interface PreparedFriendInviteShareLink extends PreparedFriendInviteBase {
    transport: "share-link";
    inviteUrl: string;
}

type PreparedFriendInvite =
    PreparedFriendInviteMessage | PreparedFriendInviteShareLink;

interface UseFriendInviteSharingOptions {
    enabled: boolean;
    userId: string | null;
    preferredTransport?: FriendInviteSharingTransport;
}

class SocialFriendInviteApiError extends Error {
    constructor(
        readonly code: string,
        message: string,
    ) {
        super(message);
        this.name = "SocialFriendInviteApiError";
    }
}

function prepareErrorMessage(
    code:
        | ApiSocialFriendInviteSharePrepareErrorCode
        | ApiSocialFriendInviteShareLinkPrepareErrorCode,
) {
    switch (code) {
        case "UNAUTHORIZED":
            return i18n.t("friends.errors.signInAgain");
        case "USER_NOT_FOUND":
        case "USER_BANNED":
            return i18n.t("friends.errors.accountUnavailable");
        case "RATE_LIMITED":
            return i18n.t("friends.errors.rateLimited");
        case "INVITES_UNAVAILABLE":
            return i18n.t("friends.errors.invitesUnavailable");
        case "TELEGRAM_UNAVAILABLE":
            return i18n.t("friends.errors.telegramUnavailable");
        case "INVALID_PAYLOAD":
        case "REQUEST_CONFLICT":
        case "UNKNOWN":
            return i18n.t("friends.errors.invite");
    }
}

function redemptionErrorMessage(
    code: ApiSocialFriendInviteShareRedeemErrorCode,
) {
    switch (code) {
        case "UNAUTHORIZED":
            return i18n.t("friends.errors.signInAgain");
        case "USER_NOT_FOUND":
        case "USER_BANNED":
            return i18n.t("friends.errors.accountUnavailable");
        case "INVALID_PAYLOAD":
        case "TOKEN_INVALID":
            return i18n.t("friends.errors.invalidInvite");
        case "TOKEN_EXPIRED":
            return i18n.t("friends.errors.expiredInvite");
        case "TOKEN_REVOKED":
            return i18n.t("friends.errors.revokedInvite");
        case "SELF_INVITE":
            return i18n.t("friends.errors.selfInvite");
        case "SHARE_CAP_REACHED":
            return i18n.t("friends.errors.shareCapReached");
        case "INVITER_UNAVAILABLE":
            return i18n.t("friends.errors.inviterUnavailable");
        case "RELATIONSHIP_UNAVAILABLE":
            return i18n.t("friends.errors.relationshipUnavailable");
        case "UNKNOWN":
            return i18n.t("friends.errors.redeem");
    }
}

function readStorage(key: string) {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeStorage(key: string, value: string) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Storage is an idempotency/performance hint only; server state wins.
    }
}

function removeStorage(key: string) {
    try {
        window.localStorage.removeItem(key);
    } catch {
        // See writeStorage.
    }
}

function createUuid() {
    if (typeof crypto.randomUUID === "function") return crypto.randomUUID();

    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, "0"),
    ).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function errorMessage(error: unknown) {
    return error instanceof Error ? error.message : i18n.t("errors.generic");
}

function isUsable(
    prepared: PreparedFriendInvite | null,
): prepared is PreparedFriendInvite {
    return prepared !== null && Date.parse(prepared.expiresAt) > Date.now();
}

function isCurrentPreparedInvite(
    prepared: PreparedFriendInvite | null,
    requestIdKey: string,
) {
    if (!isUsable(prepared)) return false;
    const currentRequestId = readStorage(requestIdKey);

    // localStorage is only a cross-hook coordination hint. If it is blocked,
    // the mounted hook can still use its in-memory prepared capability.
    return currentRequestId === null || currentRequestId === prepared.requestId;
}

/**
 * Prepares a reusable, non-rewarded social invitation while the dialog is
 * open. Prefetching the preferred transport keeps the eventual ADD FRIEND tap
 * in the user-gesture stack whenever its Telegram capability is ready.
 */
export function useFriendInviteSharing({
    enabled,
    userId,
    preferredTransport = "prepared-message",
}: UseFriendInviteSharingOptions) {
    const webAppUserId = getTelegramWebApp()?.initDataUnsafe.user?.id;
    const storageScope = userId ?? webAppUserId?.toString() ?? "telegram";
    const legacyRequestIdKey = useMemo(
        () =>
            `${NAME_SPACE}social:friend-invite-share:${storageScope}:request-id`,
        [storageScope],
    );
    const requestIdKey = useMemo(
        () =>
            `${NAME_SPACE}social:friend-invite-share:${FRIEND_INVITE_SHARE_STORAGE_VERSION}:${storageScope}:request-id`,
        [storageScope],
    );
    const [error, setError] = useState<string | null>(null);
    const preparedRef = useRef<
        Record<FriendInviteSharingTransport, PreparedFriendInvite | null>
    >({
        "prepared-message": null,
        "share-link": null,
    });
    const preparePromiseRef = useRef<
        Record<
            FriendInviteSharingTransport,
            Promise<PreparedFriendInvite | null> | null
        >
    >({
        "prepared-message": null,
        "share-link": null,
    });
    const sharingRef = useRef(false);
    const mountedRef = useRef(false);
    const enabledRef = useRef(enabled);

    useEffect(() => {
        enabledRef.current = enabled;
    }, [enabled]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        // v1 reused one capability across successful share actions. Retiring
        // that persisted key ensures the first post-upgrade invite expresses
        // fresh intent and can re-friend someone who used the older link.
        removeStorage(legacyRequestIdKey);
        // Never reuse a prepared capability across authenticated user scopes.
        preparedRef.current["prepared-message"] = null;
        preparedRef.current["share-link"] = null;
    }, [legacyRequestIdKey, requestIdKey]);

    const prepare = useCallback(
        async (
            transport: FriendInviteSharingTransport,
        ): Promise<PreparedFriendInvite | null> => {
            if (!enabled) return null;
            const currentPrepared = preparedRef.current[transport];
            if (isCurrentPreparedInvite(currentPrepared, requestIdKey)) {
                return currentPrepared;
            }
            const currentPromise = preparePromiseRef.current[transport];
            if (currentPromise) return currentPromise;

            const operation = (async () => {
                if (mountedRef.current) {
                    setError(null);
                }

                const perform = async (rotateRequestId: boolean) => {
                    if (rotateRequestId) removeStorage(requestIdKey);
                    const requestSchema =
                        transport === "share-link"
                            ? ApiSocialFriendInviteShareLinkPrepareReqSchema
                            : ApiSocialFriendInviteSharePrepareReqSchema;
                    const stored = requestSchema.safeParse({
                        requestId: readStorage(requestIdKey),
                    });
                    const request = stored.success
                        ? stored.data
                        : requestSchema.parse({
                              requestId: createUuid(),
                          });
                    writeStorage(requestIdKey, request.requestId);

                    if (transport === "share-link") {
                        const { body } = await authenticatedApiCall(
                            "/api/v1/social/friend-invite-shares/prepare-link",
                            ApiSocialFriendInviteShareLinkPrepareResSchema,
                            {
                                method: "POST",
                                body: JSON.stringify(request),
                            },
                        );
                        if (body.status === "ERROR") {
                            throw new SocialFriendInviteApiError(
                                body.code,
                                prepareErrorMessage(body.code),
                            );
                        }
                        return {
                            transport,
                            requestId: request.requestId,
                            inviteUrl: body.inviteUrl,
                            expiresAt: body.share.expiresAt,
                        } satisfies PreparedFriendInviteShareLink;
                    }

                    const { body } = await authenticatedApiCall(
                        "/api/v1/social/friend-invite-shares/prepare",
                        ApiSocialFriendInviteSharePrepareResSchema,
                        {
                            method: "POST",
                            body: JSON.stringify(request),
                        },
                    );
                    if (body.status === "ERROR") {
                        throw new SocialFriendInviteApiError(
                            body.code,
                            prepareErrorMessage(body.code),
                        );
                    }
                    return {
                        transport,
                        requestId: request.requestId,
                        messageId: body.preparedMessageId,
                        expiresAt: body.preparedMessageExpiresAt,
                    } satisfies PreparedFriendInviteMessage;
                };

                try {
                    return await perform(false);
                } catch (prepareError) {
                    // A consumed requestId can outlive/reach the cap of its share.
                    // Rotate only for that explicit conflict; transport/Telegram
                    // retries must retain the original idempotency identity.
                    if (
                        prepareError instanceof SocialFriendInviteApiError &&
                        prepareError.code === "REQUEST_CONFLICT"
                    ) {
                        return perform(true);
                    }
                    throw prepareError;
                }
            })()
                .then((prepared) => {
                    if (prepared) {
                        preparedRef.current[prepared.transport] = prepared;
                    }
                    toast.dismiss(FRIEND_INVITE_SHARE_ERROR_TOAST_ID);
                    return prepared;
                })
                .catch((prepareError: unknown) => {
                    const message = errorMessage(prepareError);
                    if (mountedRef.current) setError(message);
                    toast.error(message, {
                        id: FRIEND_INVITE_SHARE_ERROR_TOAST_ID,
                    });
                    return null;
                })
                .finally(() => {
                    if (preparePromiseRef.current[transport] === operation) {
                        preparePromiseRef.current[transport] = null;
                    }
                });

            preparePromiseRef.current[transport] = operation;
            return operation;
        },
        [enabled, requestIdKey],
    );

    useEffect(() => {
        if (enabled) void prepare(preferredTransport);
    }, [enabled, preferredTransport, prepare]);

    const retirePreparedInvite = useCallback(
        (prepared: PreparedFriendInvite) => {
            // openTelegramLink has no delivery callback, so launching its
            // picker is the strongest available signal of fresh invite intent.
            const currentRequestId = readStorage(requestIdKey);
            if (
                currentRequestId === null ||
                currentRequestId === prepared.requestId
            ) {
                writeStorage(requestIdKey, createUuid());
            }

            for (const transport of [
                "prepared-message",
                "share-link",
            ] as const) {
                if (
                    preparedRef.current[transport]?.requestId ===
                    prepared.requestId
                ) {
                    preparedRef.current[transport] = null;
                }
            }
            if (mountedRef.current && enabledRef.current) {
                void prepare(preferredTransport);
            }
        },
        [preferredTransport, prepare, requestIdKey],
    );

    const shareMessage = useCallback(async () => {
        if (!enabled || sharingRef.current) return false;
        sharingRef.current = true;
        const webApp = getTelegramWebApp();
        if (
            !webApp ||
            !webApp.isVersionAtLeast("8.0") ||
            typeof webApp.shareMessage !== "function"
        ) {
            const message = i18n.t("friends.errors.telegramVersion");
            setError(message);
            toast.error(message, {
                id: FRIEND_INVITE_SHARE_ERROR_TOAST_ID,
            });
            haptics.notification("error");
            sharingRef.current = false;
            return false;
        }

        try {
            const currentPrepared = preparedRef.current["prepared-message"];
            const prepared = isCurrentPreparedInvite(
                currentPrepared,
                requestIdKey,
            )
                ? currentPrepared
                : await prepare("prepared-message");
            if (!prepared || prepared.transport !== "prepared-message") {
                return false;
            }

            setError(null);
            // When prefetch completed, this stays directly in the click stack
            // and opens Telegram's native multi-recipient picker immediately.
            webApp.shareMessage(prepared.messageId, (sent) => {
                if (!sent) return;

                haptics.notification("success");
                toast.success(i18n.t("friends.toasts.inviteSent"), {
                    id: FRIEND_INVITE_SHARE_SUCCESS_TOAST_ID,
                });
                retirePreparedInvite(prepared);
            });
            return true;
        } catch (shareError) {
            const message = errorMessage(shareError);
            setError(message);
            toast.error(message, {
                id: FRIEND_INVITE_SHARE_ERROR_TOAST_ID,
            });
            haptics.notification("error");
            return false;
        } finally {
            sharingRef.current = false;
        }
    }, [enabled, prepare, requestIdKey, retirePreparedInvite]);

    const shareViaTelegramLink = useCallback(async () => {
        if (!enabled || sharingRef.current) return false;
        sharingRef.current = true;
        const webApp = getTelegramWebApp();
        if (!webApp || typeof webApp.openTelegramLink !== "function") {
            const message = i18n.t("friends.errors.telegramShareUnavailable");
            setError(message);
            toast.error(message, {
                id: FRIEND_INVITE_SHARE_ERROR_TOAST_ID,
            });
            haptics.notification("error");
            sharingRef.current = false;
            return false;
        }

        try {
            const currentPrepared = preparedRef.current["share-link"];
            const prepared = isCurrentPreparedInvite(
                currentPrepared,
                requestIdKey,
            )
                ? currentPrepared
                : await prepare("share-link");
            if (!prepared || prepared.transport !== "share-link") {
                return false;
            }

            setError(null);
            if (
                !shareUrlViaTelegram(
                    prepared.inviteUrl,
                    i18n.t("friends.shareText"),
                )
            ) {
                throw new Error(
                    i18n.t("friends.errors.telegramShareUnavailable"),
                );
            }
            retirePreparedInvite(prepared);
            return true;
        } catch (shareError) {
            const message = errorMessage(shareError);
            setError(message);
            toast.error(message, {
                id: FRIEND_INVITE_SHARE_ERROR_TOAST_ID,
            });
            haptics.notification("error");
            return false;
        } finally {
            sharingRef.current = false;
        }
    }, [enabled, prepare, requestIdKey, retirePreparedInvite]);

    return {
        error,
        // Backwards-compatible alias used by the existing shareMessage flow.
        share: shareMessage,
        shareMessage,
        shareViaTelegramLink,
    };
}

function getFriendInviteStartParam() {
    const webAppStartParam = getTelegramWebApp()?.initDataUnsafe.start_param;
    const queryStartParam = new URLSearchParams(window.location.search).get(
        "tgWebAppStartParam",
    );
    return (
        [webAppStartParam, queryStartParam].find(
            (candidate): candidate is string =>
                typeof candidate === "string" &&
                FRIEND_INVITE_START_PARAM.test(candidate),
        ) ?? null
    );
}

// React StrictMode can remount Layout while one redemption is in flight.
const redeemingFriendInvites = new Set<string>();

/** Redeem social invite launch parameters once authentication is ready. */
export function useSocialFriendInviteRedemption(
    enabled: boolean,
    userId: string | null,
) {
    useEffect(() => {
        if (!enabled) return;
        const token = getFriendInviteStartParam();
        if (!token) return;

        const telegramUserId =
            getTelegramWebApp()?.initDataUnsafe.user?.id.toString();
        const storageScope = userId ?? telegramUserId ?? "telegram";
        const attemptKey = `${storageScope}:${token}`;
        if (redeemingFriendInvites.has(attemptKey)) return;
        redeemingFriendInvites.add(attemptKey);

        void (async () => {
            // The server is idempotent and returns the current active-friend
            // snapshot. Recheck once per Telegram launch instead of persisting
            // a client-side success marker that becomes stale after unfriend.
            const request = ApiSocialFriendInviteShareRedeemReqSchema.parse({
                token,
            });
            const { body } = await authenticatedApiCall(
                "/api/v1/social/friend-invite-shares/redeem",
                ApiSocialFriendInviteShareRedeemResSchema,
                {
                    method: "POST",
                    body: JSON.stringify(request),
                },
            );
            if (body.status === "ERROR") {
                throw new SocialFriendInviteApiError(
                    body.code,
                    redemptionErrorMessage(body.code),
                );
            }

            if (body.result === "ALREADY_REDEEMED" && body.friend === null) {
                toast.error(i18n.t("friends.toasts.inviteAlreadyUsed"), {
                    id: FRIEND_INVITE_REDEMPTION_TOAST_ID,
                });
                return;
            }

            if (body.friend) {
                const translationKey =
                    body.result === "FRIENDSHIP_CREATED"
                        ? "friends.toasts.friendAdded"
                        : "friends.toasts.alreadyFriends";
                toast.success(
                    i18n.t(translationKey, {
                        name: body.friend.user.displayName,
                    }),
                    { id: FRIEND_INVITE_REDEMPTION_TOAST_ID },
                );
            }
        })()
            .catch((redeemError: unknown) => {
                const message = errorMessage(redeemError);
                toast.error(message, {
                    id: FRIEND_INVITE_REDEMPTION_TOAST_ID,
                });
                console.warn(
                    "[useSocialFriendInviteRedemption] redemption failed",
                    redeemError,
                );
            })
            .finally(() => redeemingFriendInvites.delete(attemptKey));
    }, [enabled, userId]);
}
